import React, { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import * as XLSX from "xlsx";
import productService from "../../services/products/products.service";
import { ProductDetailModel } from "../admin/products";
import vendorService from "../../services/vendors/vendor.service";
import posService from "../../services/pos/pos.service";
import { VendorSummaryDTO } from  '../../models/vendors/vendor-model';
import { exportBatchCodePDF} from "../../components/admin/exportBatchCodePDF";
import { TabView, TabPanel } from "primereact/tabview";


const GenerateProductCode: React.FC = () => {
  const [posId, setPosId] = useState<number | null>(null);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [productId, setProductId] = useState<number | null>(null);
  const [mrp, setMrp] = useState<number | null>(null);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [gst, setGst] = useState<number>(0);
  const [packingSize, setPackingSize] = useState<number | null>(null);
  const [unit, setUnit] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [posCodes, setPosCodes] = useState<{ label: string; value: number }[]>([]);
  const [productOptions, setProductOptions] = useState<{ label: string; value: number; product: any }[]>([]);
  const [vendorOptions, setVendorOptions] = useState<{ label: string; value: number }[]>([]);
  const [batchCodeInput, setBatchCodeInput] = useState<string>("");
const [parsedPOS, setParsedPOS] = useState<number | null>(null);
const [parsedVendor, setParsedVendor] = useState<number | null>(null);
const [parsedProduct, setParsedProduct] = useState<number | null>(null);
const [volume, setVolume] = useState<number | null>(null);
const [measurement, setMeasurement] = useState<string>(""); 
const [decodedDate, setDecodedDate] = useState<string | null>(null);
const [formErrors, setFormErrors] = useState<{ pos?: string; vendor?: string; product?: string }>({});


const getMonthName = (monthStr: string): string => {
  const month = parseInt(monthStr);
  const shortMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return shortMonths[month - 1] ?? "Unknown";
};


 useEffect(() => {
  posService.getAllPOSBatch().then((response) => {
  const mapped = response.map((pos) => ({
  label: `${pos.posCode}, ${pos.address2 || pos.cityName || "Unknown"}`, // What user sees in dropdown
  value: pos.posID,
  code: `POS-${pos.posCode}`, // Custom field to extract later
}));
  setPosCodes(mapped);
});


  productService.getAllAdminProducts().then((response: ProductDetailModel[]) => {
    const filtered = response.filter((item) => !item.isDeleted);
    const mapped = filtered.map((item) => ({
      label: item.name,
      value: item.productID,
      product: item,
    }));
    setProductOptions(mapped);
  });



  vendorService.getAllVendors().then((vendors: VendorSummaryDTO[]) => {
    const mapped = vendors.map((v) => ({
      label: `${v.firmInfo}, ${v.officeContactNbr || ""}`,
      value: v.vendorID,
    }));
    setVendorOptions(mapped);
  });
}, []);


  const formatDate = (date: Date | null, format: "DDMMYY" | "Pretty") => {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear().toString();
    return format === "DDMMYY" ? `${day}${month}${year.slice(2)}` : `${day}-${d.toLocaleString("default", { month: "short" })}-${year}`;
  };

  const batchCode =
    posId && vendorId && productId && selectedDate
      ? `${posId}-${vendorId}-${productId}-${formatDate(selectedDate, "DDMMYY")}`
      : "";

 const getEffectiveSize = (volume: number | null, packingSize: number | null): number => {
  if (!volume || !packingSize) return 0;
  return volume * packingSize;
};

const effectiveVolume = getEffectiveSize(volume, packingSize);
const ratePerUnit =
  effectiveVolume && mrp
    ? `Rs. ${(mrp / effectiveVolume).toFixed(2)} Per ${measurement}`
    : "";

 const exportToExcel = () => {
  const errors: typeof formErrors = {};
  if (!posId) errors.pos = "Please select POS";
  if (!vendorId) errors.vendor = "Please select Vendor";
  if (!productId) errors.product = "Please select Product";

  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }


  setFormErrors({}); 


  

  const product = productOptions.find((p) => p.value === productId);
  const data = [
    ["Product Name", product?.label || ""],
    ["", ""],
    ["MRP (₹)", mrp],
[`Rs per ${measurement}`, ratePerUnit],
    ["Batch Code", batchCode],
    ["Packed Date", formatDate(selectedDate, "Pretty")],
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "BatchCode");
  XLSX.writeFile(wb, `ProductBatchCode_${batchCode}.xlsx`);
};


const handlePDFExport = () => {
  const errors: typeof formErrors = {};
  if (!posId) errors.pos = "Please select POS";
  if (!vendorId) errors.vendor = "Please select Vendor";
  if (!productId) errors.product = "Please select Product";

  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }

  setFormErrors({}); // clear previous errors

 exportBatchCodePDF({
  productName: productOptions.find((p) => p.value === productId)?.label || "",
  mrp: mrp || 0,
  ratePerUnit,
  batchCode,
  packedDate: formatDate(selectedDate, "Pretty"),
  measurement: measurement || "ml", // <-- this line fixes the issue
});

};

  return (
  <div className="flex justify-center mt-6 px-2">
    <TabView>
      <TabPanel header="Generate Batch Code">
        <Card className="w-full max-w-6xl shadow-2xl p-6">
          <h2 className="text-2xl font-bold mb-6 text-center text-green-800">
            Generate Batch Code
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="block mb-1 font-medium">POS</label>
            <Dropdown
  value={posId}
  options={posCodes}
  onChange={(e) => {
    setPosId(e.value);
    setFormErrors((prev) => ({ ...prev, pos: undefined }));
  }}
  placeholder="Select POS"
  className="w-full"
  filter
  filterBy="label"
  filterMatchMode="contains"
/>
{formErrors.pos && <small className="text-red-500">{formErrors.pos}</small>}

            </div>

            <div>
              <label className="block mb-1 font-medium">Vendor</label>
            <Dropdown
  value={vendorId}
  options={vendorOptions}
  onChange={(e) => {
    setVendorId(e.value);
    setFormErrors((prev) => ({ ...prev, vendor: undefined }));
  }}
  placeholder="Select Vendor"
  className="w-full"
  filter
  filterBy="label"
  filterMatchMode="contains"
/>
{formErrors.vendor && <small className="text-red-500">{formErrors.vendor}</small>}

            </div>

            <div>
              <label className="block mb-1 font-medium">Product</label>
              <Dropdown
  value={productId}
  options={productOptions}
  onChange={(e) => {
    setProductId(e.value);
    setFormErrors((prev) => ({ ...prev, product: undefined }));
    const selected = productOptions.find((p) => p.value === e.value);
    if (selected?.product) {
      const product = selected.product;
      setPackingSize(product.packingSize ?? 1);
      setVolume(product.volume ?? 0);
      setUnitPrice(product.unitPrice ?? 0);
      setGst(product.gst ?? 0);
      setMeasurement(product.measurement ?? "ml");
      const calculatedMrp = product.unitPrice * (product.packingSize ?? 1) * (1 + (product.gst ?? 0) / 100);
      setMrp(Math.ceil(calculatedMrp));
    }
  }}
  placeholder="Search Product"
  className="w-full"
  filter
  filterBy="label"
  filterMatchMode="contains"
/>
{formErrors.product && <small className="text-red-500">{formErrors.product}</small>}

            </div>







            <div>
              <label className="block mb-1 font-medium">Packing Size</label>
              <InputText
                value={packingSize !== null ? packingSize.toString() : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = parseFloat(val);
                  if (val === "") setPackingSize(null);
                  else if (!isNaN(parsed)) {
                    setPackingSize(parsed);
                    const newMrp = unitPrice * parsed * (1 + gst / 100);
                    setMrp(Math.ceil(newMrp));
                  }
                }}
                placeholder="e.g. 500"
                className="w-full"
              />
            </div>
 <div>
              <label className="block mb-1 font-medium">MRP (₹)</label>
              <InputText value={mrp !== null ? mrp.toString() : ""} disabled className="w-full" />
            </div>

            <div>
              <label className="block mb-1 font-medium">Volume</label>
              <InputText value={volume !== null ? volume.toString() : ""} disabled className="w-full" placeholder="Auto-filled from product" />
            </div>

            <div>
              <label className="block mb-1 font-medium">Measurement</label>
              <InputText value={measurement} disabled className="w-full" placeholder="Auto-filled from product" />
            </div>

            
            <div>
              <label className="block mb-1 font-medium">Packed Date</label>
              <Calendar value={selectedDate} onChange={(e) => setSelectedDate(e.value as Date)} showIcon dateFormat="dd-mm-yy" className="w-full" />
            </div>
            
          </div>

          {batchCode && mrp && ratePerUnit && selectedDate && packingSize && (
            <div className="mt-6 bg-green-50 border border-green-300 p-4 rounded shadow">
              <h4 className="text-lg font-semibold mb-2">Printable Code</h4>
              <p><strong>MRP:</strong> ₹{mrp}</p>
              <p><strong>Rs per {measurement}:</strong> {ratePerUnit}</p>
              <p><strong>Batch Code:</strong> {batchCode}</p>
              <p><strong>Packed Date:</strong> {formatDate(selectedDate, "Pretty")}</p>
            </div>
          )}

       <div className="mt-6 flex gap-4 justify-end pr-10">
  <Button
    label="Reset"
    icon="pi pi-refresh"
    className="p-button-warning px-5 py-2 text-base"
    onClick={() => {
      setPosId(null);
      setVendorId(null);
      setProductId(null);
      setPackingSize(null);
      setVolume(null);
      setMeasurement("");
      setMrp(null);
      setUnitPrice(0);
      setGst(0);
      setSelectedDate(new Date());
      setBatchCodeInput("");
      setParsedPOS(null);
      setParsedVendor(null);
      setParsedProduct(null);
      setDecodedDate(null);
      setFormErrors({});
    }}
  />

  <Button
    label="Export to Excel"
    icon="pi pi-file-excel"
    className="p-button-secondary px-5 py-2 text-base"
    onClick={exportToExcel}
  />

  <Button
    label="Export to PDF"
    icon="pi pi-file-pdf"
    className="p-button-danger px-5 py-2 text-base"
    onClick={handlePDFExport}
  />
</div>
        </Card>
      </TabPanel>

      <TabPanel header="Track Batch Code">
        <Card className="w-full max-w-4xl shadow-2xl p-6">
          <h3 className="text-xl font-bold mb-4 text-green-800">Track Batch Code</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block mb-1 font-medium">Enter Batch Code</label>
              <InputText
  className="w-full"
  value={batchCodeInput}
  onChange={(e) => {
    const rawVal = e.target.value;


    const cleanedVal = rawVal.replace(/[^0-9-]/g, "");

    setBatchCodeInput(cleanedVal);

    const parts = cleanedVal.split("-");
    if (parts.length >= 4) {
      const [pos, vendor, product, dateStr] = parts;
      const posID = parseInt(pos);
      const vendorID = parseInt(vendor);
      const productID = parseInt(product);

      if (!isNaN(posID) && !isNaN(vendorID) && !isNaN(productID)) {
        setParsedPOS(posID);
        setParsedVendor(vendorID);
        setParsedProduct(productID);
      } else {
        setParsedPOS(null);
        setParsedVendor(null);
        setParsedProduct(null);
      }

      if (dateStr.length === 6) {
        const dd = dateStr.slice(0, 2);
        const mm = dateStr.slice(2, 4);
        const yy = dateStr.slice(4, 6);
        const formatted = `${dd}-${getMonthName(mm)}-20${yy}`;
        setDecodedDate(formatted);
      } else {
        setDecodedDate(null);
      }
    } else {
      setParsedPOS(null);
      setParsedVendor(null);
      setParsedProduct(null);
      setDecodedDate(null);
    }
  }}
  placeholder="e.g. 1-2-5-080725"
/>

            </div>
          </div>

         {parsedPOS && parsedVendor && parsedProduct && (
  <div className="p-4 bg-gray-50 border border-gray-300 rounded-md shadow">
    <p><strong>POS:</strong> {posCodes.find((p) => p.value === parsedPOS)?.label || `ID ${parsedPOS}`}</p>
    <p><strong>Vendor:</strong> {vendorOptions.find((v) => v.value === parsedVendor)?.label || `ID ${parsedVendor}`}</p>
    <p><strong>Product:</strong> {productOptions.find((p) => p.value === parsedProduct)?.label || `ID ${parsedProduct}`}</p>
    {decodedDate && (
      <p><strong>Packed Date:</strong> {decodedDate}</p>
    )}
  </div>
)}
        </Card>
      </TabPanel>
    </TabView>
  </div>
);
};
export default GenerateProductCode;
